/**
 * Product Personalizer DUAL-ENGRAVE — Shopify Block Script
 *
 * For products with laser engraving on plates (two-sided personalization).
 * Each side: customer chooses Design or Text.
 * Text = free textarea with custom font, auto-shrink on canvas.
 * Design = select from a pre-configured design gallery per side.
 *
 * Shape selector determines the plate shape (dogtag, circle, etc.).
 * Each shape has its own color variants with 4 product images
 * (imgDesignText, imgTextDesign, imgDesignDesign, imgTextText).
 *
 * Canvas order: product image (full) -> design PNGs on top -> text on top.
 * Text color is admin-controlled per product color (activeColor.textColor).
 *
 * Integrates into the product form by injecting hidden inputs.
 * Triple interceptor: form submit + fetch + XHR (for ReelAsIt).
 */

(function() {
  'use strict';

  // JPEG no tiene transparencia: lo transparente se vuelve NEGRO al exportar. Antes de
  // exportar se calca el canvas sobre blanco (si ya era opaco, no cambia nada).
  // Bug real 05/08/2026: los fondos con transparencia del Tag de Mascota se guardaban oscuros.
  function __pzSobreBlanco(cv) {
    var c = document.createElement('canvas');
    c.width = cv.width; c.height = cv.height;
    var x = c.getContext('2d');
    x.fillStyle = '#ffffff';
    x.fillRect(0, 0, c.width, c.height);
    x.drawImage(cv, 0, 0);
    return c;
  }


  // ============================================================
  // CLASS
  // ============================================================
  class ProductPersonalizerDualEngrave {
    constructor(el) {
      this.el = el;
      this.config = null;
      this.productForm = null;
      this._previewUrl = null;

      // Single canvas
      this.canvas = el.querySelector('[data-pzd-canvas]');
      this.ctx = this.canvas ? this.canvas.getContext('2d') : null;

      // Product image (from 4-combo keys)
      this._bgImg = null;
      this._bgImages = {}; // cache: "shapeId_colorId_imgKey" -> Image

      // Design images cache: "designId_colorId" -> Image
      this._designImages = {};

      // Per-side state
      this.sideState = {
        front: { mode: 'design', text: '', design: null },
        back:  { mode: 'text',   text: '', design: null }
      };

      // Shared state
      this.state = {
        activeSide: 'front',
        shape: null,
        color: null,
        font: null,
        variantId: null
      };

      // Font loading tracker
      this._fontsLoaded = {};

      this._init();
    }

    // ============================================================
    // INIT
    // ============================================================
    _init() {
      try {
        this.config = JSON.parse(this.el.dataset.pzdConfig);
      } catch(e) {
        console.error('[PZD-ENG] Invalid config:', e);
        return;
      }

      if (this.config.type !== 'dual-engrave') return;

      var sidesConfig = this.config.sides || {};

      // Text-only mode: both sides are locked to text, no design toggle at all
      this._textOnly = this.config.textOnly === true;

      // Variantes de Shopify (expuestas por el liquid) — stock real por Forma + Color
      try {
        this.shopifyVariants = JSON.parse(this.el.dataset.pzdVariants || '[]');
      } catch (e) {
        this.shopifyVariants = [];
      }
      try {
        this.shopifyOptions = JSON.parse(this.el.dataset.pzdOptions || '[]');
      } catch (e) {
        this.shopifyOptions = [];
      }
      // Disponibilidad global del producto (para productos simples sin variantes)
      this.productAvailable = this.el.dataset.pzdProductAvailable === 'true';
      this.variantMap = {};
      this._buildVariantMap();

      // Set default modes from config
      var frontCfg = sidesConfig.front || {};
      var backCfg = sidesConfig.back || {};
      this.sideState.front.mode = this._textOnly ? 'text' : (frontCfg.defaultMode || 'design');
      this.sideState.back.mode = this._textOnly ? 'text' : (backCfg.defaultMode || 'text');

      // Hide the Design/Text toggle of both sides when text-only
      if (this._textOnly) {
        this.el.querySelectorAll('[data-pzd-mode-toggle]').forEach(function(t) {
          t.style.display = 'none';
        });
      }

      // Variant ID
      this.state.variantId = this.el.dataset.pzdVariantId || null;

      // First available shape (preferir una que tenga algún color con stock)
      var self0 = this;
      var shapes = this.config.shapes || [];
      var firstShape = shapes.find(function(s) {
        return s.available !== false && self0._shapeHasAnyAvailableColor(s);
      });
      if (!firstShape) firstShape = shapes.find(function(s) { return s.available !== false; });
      this.state.shape = firstShape ? firstShape.id : null;

      // First available color within active shape (preferir uno con stock real)
      if (firstShape) {
        var shapeColors = firstShape.colors || [];
        var firstColor = shapeColors.find(function(c) {
          return c.available !== false && self0._isVariantAvailable(firstShape.name, c.name);
        });
        if (!firstColor) firstColor = shapeColors.find(function(c) { return c.available !== false; });
        this.state.color = firstColor ? firstColor.id : null;
      }

      // Font
      var fonts = this.config.fonts || [];
      this.state.font = fonts.length > 0 ? fonts[0].family : null;

      // Load custom fonts
      this._loadFonts();

      // Apply per-product styles
      this._applyStyles();

      // Show/hide sections
      this._showSections();

      // Render shared sections
      if (this._sectionVisible('shapes')) this._renderShapeSelector();
      if (this._sectionVisible('colors')) this._renderColors();
      if (this._sectionVisible('fonts'))  this._renderFontSelector();

      // Render design galleries per side (skipped in text-only mode)
      if (!this._textOnly) {
        this._renderDesignGallery('front');
        this._renderDesignGallery('back');
      }

      // Update labels from config
      this._updateLabels();

      // Set initial badges (must be after _updateLabels which rebuilds the label DOM)
      this._updateBadge();
      this._updateShapeBadge();
      this._updateDesignBadge('front');
      this._updateDesignBadge('back');

      // Setup modes from config defaults
      this._initModes();

      // Setup accordion panels (horizontal tabs Adelante/Atras)
      this._setupAccordion();

      // Find product form and integrate
      this.productForm = this._findProductForm();
      if (this.productForm) {
        this._injectHiddenInputs();
        this._interceptFormSubmit();
      }

      // Stock real de Shopify (solo si el sync está activo)
      this._hideThemeVariantSelectors();
      this._updateVariantId();
      this._checkGlobalStock();

      // ReelAsIt interceptor
      this._interceptReelAsItButton();

      // AJAX interceptors
      this._interceptFetchAddToCart();
      this._interceptXHRAddToCart();

      // Load product image
      this._loadProductImage();

      // Bind events
      this._bindEvents();
      this._updateStatus();

      // Mark as loaded
      this.el.classList.add('is-loaded');
    }

    // ============================================================
    // SHOPIFY VARIANTS — stock real por Forma + Color
    // ============================================================
    _buildVariantMap() {
      var self = this;
      var shapeOpt = (this.config.shopifyShapeOption || 'Forma').toLowerCase();
      var colorOpt = (this.config.shopifyColorOption || 'Color').toLowerCase();

      (this.shopifyVariants || []).forEach(function(v) {
        var shape = null, color = null;
        if (v.option1 !== undefined) {
          // Formato posicional: option1/2/3 según el orden de product.options
          var opts = self.shopifyOptions || [];
          [v.option1, v.option2, v.option3].forEach(function(val, idx) {
            if (!val || !opts[idx]) return;
            var optName = (opts[idx].name || '').toLowerCase();
            if (optName === shapeOpt) shape = val;
            if (optName === colorOpt) color = val;
          });
        } else if (Array.isArray(v.options)) {
          v.options.forEach(function(o) {
            var optName = (o.name || '').toLowerCase();
            if (optName === shapeOpt) shape = o.value;
            if (optName === colorOpt) color = o.value;
          });
        }
        if (color) {
          // Clave "forma|color". Si el producto solo tiene opción Color, la forma va vacía.
          var key = (shape || '') + '|' + color;
          self.variantMap[key] = { id: v.id, available: v.available !== false };
        }
      });
    }

    // ¿Producto simple (sin variantes reales, solo "Default Title")?
    _isSimpleProduct() {
      var opts = this.shopifyOptions || [];
      if (opts.length === 0) return true;
      if (opts.length === 1 && (opts[0].name || '').toLowerCase() === 'title') return true;
      return false;
    }

    _isVariantAvailable(shapeName, colorName) {
      // 1) Sync desactivado en el admin → siempre disponible (vende sin control de stock).
      if (this.config.enableStockSync !== true) return true;
      // 2) Producto simple (sin variantes) → usar el stock GLOBAL del producto.
      if (this._isSimpleProduct()) return this.productAvailable;
      // 3) Sync activo pero no se mapeó ninguna variante (nombres que no coinciden) → no bloquear la venta.
      //    El aviso de "mapeo incompleto" sale en el admin, no acá.
      if (Object.keys(this.variantMap).length === 0) return true;
      if (!colorName) return false;
      // 4) Buscar por "forma|color" y, si el producto no tiene opción de forma, por "|color".
      var v = this.variantMap[(shapeName || '') + '|' + colorName];
      if (!v) v = this.variantMap['|' + colorName];
      return v ? v.available : false;
    }

    // ¿La forma tiene al menos un color con stock?
    _shapeHasAnyAvailableColor(shape) {
      if (!shape) return false;
      var self = this;
      var colors = shape.colors || [];
      if (colors.length === 0) return true;
      return colors.some(function(c) {
        return c.available !== false && self._isVariantAvailable(shape.name, c.name);
      });
    }

    _getVariantId(shapeName, colorName) {
      if (!colorName) return null;
      var v = this.variantMap[(shapeName || '') + '|' + colorName];
      if (!v) v = this.variantMap['|' + colorName];
      return v ? v.id : null;
    }

    _updateVariantId() {
      if (this.config.enableStockSync !== true) return;
      var shape = this._getActiveShape();
      var color = this._getActiveColor();
      if (!color) return;
      var id = this._getVariantId(shape ? shape.name : '', color.name);
      if (!id) return;
      this.state.variantId = id;
      // Actualizar también el input name="id" del form de Shopify
      if (this.productForm) {
        var idInput = this.productForm.querySelector('input[name="id"], select[name="id"]');
        if (idInput) {
          idInput.value = id;
          // Avisar al theme por si escucha el cambio (algunos actualizan el precio con esto)
          try { idInput.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
        }
      }
      this._injectHiddenInputs();
    }

    // Si es producto simple, el sync está activo y el stock global es 0 → aviso persistente.
    _checkGlobalStock() {
      if (this.config.enableStockSync === true && this._isSimpleProduct() && !this.productAvailable) {
        var box = this.el.querySelector('[data-pzd-msg="error"]');
        if (box) {
          box.textContent = 'Producto agotado temporalmente.';
          box.classList.add('is-visible');
        }
      }
    }

    _hideThemeVariantSelectors() {
      if (this.config.enableStockSync !== true) return;

      // Selectores de variantes del theme: suelen estar FUERA del form del producto.
      var optionInputs = document.querySelectorAll(
        'input[name^="options"], input[name^="Options"], ' +
        'select[name^="options"], select[name^="Options"]'
      );
      optionInputs.forEach(function(input) {
        var wrapper = input.closest(
          'fieldset, variant-radios, variant-selects, ' +
          '.product-form__input, .product-form__option, .product-options, ' +
          '.variant-input-wrap, .swatch, [data-product-option]'
        );
        if (wrapper) wrapper.style.display = 'none';
      });

      document.querySelectorAll(
        'variant-radios, variant-selects, ' +
        '.product-form__variants, .product-form__option-selectors, ' +
        '.product-info__variant-picker, [data-variant-selector], ' +
        '.vvp'  // theme Toto V.7 (Mistikal) - variant visual picker propio
      ).forEach(function(el) {
        el.style.display = 'none';
      });
    }

    // ============================================================
    // LOAD CUSTOM FONTS (from config fonts[].src)
    // ============================================================
    _loadFonts() {
      var self = this;
      var fonts = this.config.fonts || [];

      fonts.forEach(function(f) {
        if (!f.src || self._fontsLoaded[f.family]) return;

        try {
          var fontFace = new FontFace(f.family, 'url(' + f.src + ')');
          fontFace.load().then(function(loaded) {
            document.fonts.add(loaded);
            self._fontsLoaded[f.family] = true;
            // Update textarea font if this is the current font
            if (self.state.font === f.family) {
              self._updateTextareaFonts();
            }
            // Redraw canvas
            self._drawCanvas();
          }).catch(function(err) {
            console.warn('[PZD-ENG] Font load failed:', f.family, err);
          });
        } catch(err) {
          console.warn('[PZD-ENG] FontFace error:', f.family, err);
        }
      });
    }

    // ============================================================
    // INIT MODES — set initial mode per side from config
    // ============================================================
    _initModes() {
      var sideKeys = ['front', 'back'];
      for (var s = 0; s < sideKeys.length; s++) {
        var key = sideKeys[s];
        this._applyMode(key, this.sideState[key].mode);
      }
    }

    _applyMode(sideKey, mode) {
      if (this._textOnly) mode = 'text';
      this.sideState[sideKey].mode = mode;

      // Update mode toggle buttons
      var toggle = this.el.querySelector('[data-pzd-mode-toggle="' + sideKey + '"]');
      if (toggle) {
        toggle.querySelectorAll('[data-pzd-mode]').forEach(function(btn) {
          btn.classList.toggle('is-active', btn.dataset.pzdMode === mode);
        });
      }

      // Show/hide design/text content + font picker
      var sideEl = this.el.querySelector('[data-pzd-panel-body="' + sideKey + '"]');
      var textContent = this.el.querySelector('[data-pzd-text-content="' + sideKey + '"]');
      var designContent = this.el.querySelector('[data-pzd-design-content="' + sideKey + '"]');

      if (textContent) textContent.style.display = mode === 'text' ? '' : 'none';
      if (designContent) designContent.style.display = mode === 'design' ? '' : 'none';

      // Show/hide font picker inside this side (only in text mode + 2+ fonts)
      if (sideEl) {
        var fontSection = sideEl.querySelector('[data-pzd-section="fonts"]');
        if (fontSection) {
          var fontsEnabled = (this.config.fonts || []).length > 1;
          fontSection.style.display = (mode === 'text' && fontsEnabled) ? '' : 'none';
        }
      }

      // Update textarea font
      if (mode === 'text') {
        this._updateTextareaFont(sideKey);
        this._updateCharCount(sideKey);
      }

      // Reload product image (image key changes based on mode combination)
      this._loadProductImage();
    }

    // ============================================================
    // SHAPE HELPERS
    // ============================================================
    _getActiveShape() {
      var shapes = this.config.shapes || [];
      var self = this;
      return shapes.find(function(s) { return s.id === self.state.shape; }) || null;
    }

    _getActiveColor() {
      var shape = this._getActiveShape();
      if (!shape) return null;
      var colors = shape.colors || [];
      var self = this;
      return colors.find(function(c) { return c.id === self.state.color; }) || null;
    }

    // ============================================================
    // IMAGE KEY — based on front/back mode combination
    // ============================================================
    _getImageKey() {
      var fm = this.sideState.front.mode;
      var bm = this.sideState.back.mode;
      return 'img' +
        (fm === 'design' ? 'Design' : 'Text') +
        (bm === 'design' ? 'Design' : 'Text');
    }

    _getImageUrl() {
      var color = this._getActiveColor();
      if (!color) return '';
      var key = this._getImageKey();
      return color[key] || '';
    }

    // ============================================================
    // LOAD PRODUCT IMAGE (4-combo based on mode + color)
    // ============================================================
    _loadProductImage() {
      var url = this._getImageUrl();
      if (!url) {
        this._bgImg = null;
        this._drawCanvas();
        var loading = this.el.querySelector('[data-pzd-loading]');
        if (loading) loading.classList.add('is-hidden');
        return;
      }

      var cacheKey = this.state.shape + '_' + this.state.color + '_' + this._getImageKey();

      // Check cache
      if (this._bgImages[cacheKey]) {
        this._bgImg = this._bgImages[cacheKey];
        this._drawCanvas();
        var loading = this.el.querySelector('[data-pzd-loading]');
        if (loading) loading.classList.add('is-hidden');
        return;
      }

      var self = this;
      var loading = this.el.querySelector('[data-pzd-loading]');
      if (loading) loading.classList.remove('is-hidden');

      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() {
        self._bgImages[cacheKey] = img;
        self._bgImg = img;
        self._drawCanvas();
        if (loading) loading.classList.add('is-hidden');
      };
      img.onerror = function() {
        console.warn('[PZD-ENG] Product image failed:', url);
        self._bgImg = null;
        self._drawCanvas();
        if (loading) loading.classList.add('is-hidden');
      };
      img.src = url;
    }

    // ============================================================
    // DESIGN IMAGE LOADING
    // ============================================================
    _loadDesignImage(designId, colorId, callback) {
      var cacheKey = designId + '_' + colorId;

      // Check cache
      if (this._designImages[cacheKey]) {
        if (callback) callback(this._designImages[cacheKey]);
        return;
      }

      // Find design config
      var designs = this.config.designs || [];
      var design = designs.find(function(d) { return d.id === designId; });
      if (!design || !design.images || !design.images[colorId]) {
        if (callback) callback(null);
        return;
      }

      var url = design.images[colorId];
      var self = this;
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() {
        self._designImages[cacheKey] = img;
        if (callback) callback(img);
      };
      img.onerror = function() {
        console.warn('[PZD-ENG] Design image failed:', url);
        if (callback) callback(null);
      };
      img.src = url;
    }

    // ============================================================
    // DESIGN AREA RESOLUTION — per-design per-shape, then per-color, then default
    // ============================================================
    _getDesignArea(sideKey) {
      var defaults = { x: 0.05, y: 0.05, width: 0.90, height: 0.90 };

      // Get the active side's selected design
      var ss = this.sideState[sideKey];
      if (ss.design) {
        var designs = this.config.designs || [];
        var design = designs.find(function(d) { return d.id === ss.design; });

        // Priority 1: per-design per-shape area
        if (design && design.areas && design.areas[this.state.shape] && design.areas[this.state.shape][sideKey]) {
          return design.areas[this.state.shape][sideKey];
        }
      }

      // Priority 2: per-color area
      var color = this._getActiveColor();
      if (color && color.areas && color.areas[sideKey] && color.areas[sideKey].design) {
        return color.areas[sideKey].design;
      }

      return defaults;
    }

    // ============================================================
    // TEXT AREA RESOLUTION — per-color, then sides config, then default
    // ============================================================
    _getTextArea(sideKey) {
      var defaults = { x: 0.10, y: 0.15, width: 0.80, height: 0.70 };

      // Priority 1: per-color area
      var color = this._getActiveColor();
      if (color && color.areas && color.areas[sideKey] && color.areas[sideKey].text) {
        return color.areas[sideKey].text;
      }

      // Priority 2: sides config fallback
      var sidesConfig = this.config.sides || {};
      var sideCfg = sidesConfig[sideKey] || {};
      var textCfg = sideCfg.text || {};
      if (textCfg.area) return textCfg.area;

      return defaults;
    }

    // ============================================================
    // TABS — switch between front/back
    // ============================================================
    _setupAccordion() {
      var self = this;
      var btns = this.el.querySelectorAll('[data-pzd-panel-btn]');
      btns.forEach(function(btn) {
        btn.addEventListener('click', function() {
          self._switchPanel(btn.dataset.pzdPanelBtn);
        });
      });
    }

    _switchPanel(sideKey) {
      this.state.activeSide = sideKey;

      // Toggle tab buttons: active on clicked, inactive on others
      this.el.querySelectorAll('[data-pzd-panel-btn]').forEach(function(btn) {
        btn.classList.toggle('is-active', btn.dataset.pzdPanelBtn === sideKey);
      });

      // Toggle panels: open clicked, close others
      this.el.querySelectorAll('[data-pzd-panel]').forEach(function(p) {
        p.classList.toggle('is-open', p.dataset.pzdPanel === sideKey);
      });

      // Redraw canvas to reflect active side
      this._drawCanvas();
    }

    // Alias for compatibility
    _switchSide(sideKey) {
      this._switchPanel(sideKey);
    }

    // ============================================================
    // FIND PRODUCT FORM
    // ============================================================
    _findProductForm() {
      var formId = this.el.dataset.pzdFormId;
      if (formId) {
        var form = document.getElementById(formId);
        if (form) return form;
      }

      var selectors = [
        'form[action="/cart/add"]',
        'form[action^="/cart/add"]',
        'product-form form',
        'form.feature-product-form',
        'form[data-type="add-to-cart-form"]'
      ];

      for (var i = 0; i < selectors.length; i++) {
        var form = document.querySelector(selectors[i]);
        if (form) return form;
      }

      return null;
    }

    // ============================================================
    // HIDDEN INPUTS — inject properties into form
    // ============================================================
    _injectHiddenInputs() {
      var container = this.el.querySelector('[data-pzd-hidden-inputs]');
      if (!container) return;

      container.innerHTML = '';

      var props = this._buildProperties();
      var formId = this.productForm ? this.productForm.id : null;

      for (var key in props) {
        if (!props.hasOwnProperty(key)) continue;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + key + ']';
        input.value = props[key];
        input.setAttribute('data-pzd-property', '');
        if (formId) input.setAttribute('form', formId);
        container.appendChild(input);
      }
    }

    // ============================================================
    // BUILD PROPERTIES
    // ============================================================
    _buildProperties() {
      var props = {};
      var self = this;
      var sidesConfig = this.config.sides || {};
      var cl = this.config.cartLabels || {};

      // Shape
      var shape = this._getActiveShape();
      if (shape) {
        var shapeKey = cl.shape || this.config.shapeLabel || 'Forma';
        props[shapeKey] = shape.name;
      }

      // Color
      var color = this._getActiveColor();
      if (color) {
        var colorKey = cl.color || this.config.colorLabel || 'Color';
        props[colorKey] = color.name;
      }

      // Font (shared, only if 2+ fonts)
      var fonts = this.config.fonts || [];
      if (fonts.length > 1 && this.state.font) {
        var fontCfg = fonts.find(function(f) { return f.family === self.state.font; });
        var fontKey = cl.font || this.config.fontLabel || 'Tipografía';
        props[fontKey] = fontCfg ? (fontCfg.label || fontCfg.family) : this.state.font;
      }

      // Per side
      var sideKeys = ['front', 'back'];
      var fontConfig = this._getFontConfig();
      for (var s = 0; s < sideKeys.length; s++) {
        var key = sideKeys[s];
        var sideCfg = sidesConfig[key] || {};
        var sideLabel = sideCfg.label || (key === 'front' ? 'Adelante' : 'Atrás');
        var ss = this.sideState[key];

        if (ss.mode === 'text') {
          var textKey = cl[key + 'Text'] || (sideLabel + ' (Texto)');
          props[textKey] = fontConfig.uppercase !== false ? ss.text.toUpperCase() : ss.text;
        } else if (ss.mode === 'design') {
          if (ss.design) {
            var designs = this.config.designs || [];
            var designCfg = designs.find(function(d) { return d.id === ss.design; });
            var designKey = cl[key + 'Design'] || (sideLabel + ' (Diseño)');
            props[designKey] = designCfg ? designCfg.name : ss.design;
          }
        }
      }

      // Preview URL
      if (this._previewUrl) {
        props['_Vista Previa'] = this._previewUrl;
      }

      // Código único para el botón "Ver mi diseño" del carrito (derivado del preview).
      // Se pega a la ÚLTIMA propiedad visible → el botón queda al FINAL de la lista.
      if (this._previewUrl) {
        var _cref = String(this._previewUrl).replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase();
        var _cvis = Object.keys(props).filter(function (k) { return k.charAt(0) !== '_'; });
        if (_cref && _cvis.length) {
          props[_cvis[_cvis.length - 1]] += ' (#' + _cref + ')';
          props['_ref'] = _cref;
        }
      }

      return props;
    }

    // ============================================================
    // INTERCEPT FORM SUBMIT
    // ============================================================
    _interceptFormSubmit() {
      if (!this.productForm) return;
      var self = this;

      this.productForm.addEventListener('submit', function(e) {
        self._injectHiddenInputs();

        if (!self._validate()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }
      }, true);
    }

    // ============================================================
    // INTERCEPT FETCH — for AJAX add-to-cart
    // ============================================================
    _interceptFetchAddToCart() {
      var self = this;
      var originalFetch = window.fetch;

      window.fetch = function(url, options) {
        var fetchCtx = this;
        var urlStr = typeof url === 'string' ? url : (url instanceof Request ? url.url : '');
        if (!urlStr.includes('/cart/add')) {
          return originalFetch.apply(fetchCtx, arguments);
        }

        if (!options || !options.body) {
          return originalFetch.apply(fetchCtx, arguments);
        }

        // Validate before allowing add-to-cart
        self._injectHiddenInputs();
        if (!self._validate()) {
          self.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return Promise.reject(new Error('Personalización incompleta'));
        }

        var pzProps = self._buildProperties();

        if (options.body instanceof FormData) {
          for (var key in pzProps) {
            options.body.set('properties[' + key + ']', pzProps[key]);
          }
        } else if (typeof options.body === 'string') {
          var injected = false;
          try {
            var data = JSON.parse(options.body);
            if (typeof data === 'object' && data !== null) {
              data.properties = Object.assign({}, data.properties || {}, pzProps);
              options = Object.assign({}, options, { body: JSON.stringify(data) });
              injected = true;
            }
          } catch(err) {}

          if (!injected && options.body.indexOf('=') !== -1) {
            var params = new URLSearchParams(options.body);
            for (var key in pzProps) {
              params.set('properties[' + key + ']', pzProps[key]);
            }
            options = Object.assign({}, options, { body: params.toString() });
          }
        }

        return originalFetch.apply(fetchCtx, [url, options]);
      };
    }

    // ============================================================
    // INTERCEPT XMLHttpRequest — for ReelAsIt and other apps
    // ============================================================
    _interceptXHRAddToCart() {
      var self = this;
      var originalOpen = XMLHttpRequest.prototype.open;
      var originalSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function(method, url) {
        this._pzdUrl = typeof url === 'string' ? url : '';
        this._pzdMethod = method;
        return originalOpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function(body) {
        if (!this._pzdUrl || !this._pzdUrl.includes('/cart/add')) {
          return originalSend.apply(this, arguments);
        }

        if (!body) {
          return originalSend.apply(this, arguments);
        }

        // Validate before allowing add-to-cart
        self._injectHiddenInputs();
        if (!self._validate()) {
          self.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.abort();
          return;
        }

        var pzProps = self._buildProperties();

        if (body instanceof FormData) {
          for (var key in pzProps) {
            body.set('properties[' + key + ']', pzProps[key]);
          }
          return originalSend.apply(this, [body]);
        }

        if (typeof body === 'string') {
          var injected = false;
          try {
            var data = JSON.parse(body);
            if (typeof data === 'object' && data !== null) {
              data.properties = Object.assign({}, data.properties || {}, pzProps);
              body = JSON.stringify(data);
              injected = true;
            }
          } catch(err) {}

          if (!injected && body.indexOf('=') !== -1) {
            var params = new URLSearchParams(body);
            for (var key in pzProps) {
              params.set('properties[' + key + ']', pzProps[key]);
            }
            body = params.toString();
          }
        }

        return originalSend.apply(this, [body]);
      };
    }

    // ============================================================
    // INTERCEPT REELAIST BUTTON
    // ============================================================
    _interceptReelAsItButton() {
      var self = this;

      document.addEventListener('click', function(e) {
        var rsiBtn = e.target.closest('._rsi-buy-now-button, [class*="_rsi-buy-now"]');
        if (!rsiBtn) return;

        var insideModal = e.target.closest('#_rsi-cod-form-modal');
        if (insideModal) return;

        // Si el boton NO esta en la misma seccion de producto que el personalizador,
        // dejarlo pasar (ej: boton de ReelAsIt en el slider/drawer del carrito)
        var productSection = self.el.closest('[data-section-id], .shopify-section, section');
        if (productSection && !productSection.contains(rsiBtn)) return;

        // Re-inject before validation
        self._injectHiddenInputs();

        if (!self._validate()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, true);
    }

    // ============================================================
    // SECTION VISIBILITY
    // ============================================================
    _showSections() {
      var shapes = this.config.shapes || [];

      // Shapes section
      var shapeSection = this.el.querySelector('[data-pzd-section="shapes"]');
      if (shapeSection && shapes.length > 0) {
        shapeSection.style.display = '';
      }

      // Colors section (colors inside active shape)
      var colorSection = this.el.querySelector('[data-pzd-section="colors"]');
      if (colorSection) {
        var activeShape = this._getActiveShape();
        var shapeColors = activeShape ? (activeShape.colors || []) : [];
        colorSection.style.display = shapeColors.length > 0 ? '' : 'none';
      }

      // Font sections are managed by _applyMode (shown only in text mode when 2+ fonts)
    }

    _sectionVisible(name) {
      if (name === 'fonts') {
        return (this.config.fonts || []).length > 1;
      }
      if (name === 'shapes') {
        return (this.config.shapes || []).length > 0;
      }
      if (name === 'colors') {
        var activeShape = this._getActiveShape();
        return activeShape && (activeShape.colors || []).length > 0;
      }
      var section = this.el.querySelector('[data-pzd-section="' + name + '"]');
      return section && section.style.display !== 'none';
    }

    // ============================================================
    // UPDATE LABELS FROM CONFIG
    // ============================================================
    _updateLabels() {
      var self = this;
      var sidesConfig = this.config.sides || {};

      // Shape label
      if (this.config.shapeLabel !== undefined) {
        var shapeLbl = this.el.querySelector('[data-pzd-shape-label]');
        if (shapeLbl) {
          var shapeBadge = shapeLbl.querySelector('[data-pzd-shape-name]');
          shapeLbl.textContent = (this.config.shapeLabel || 'Forma') + ' ';
          if (shapeBadge) shapeLbl.appendChild(shapeBadge);
        }
      }

      // Color label
      if (this.config.colorLabel) {
        var lbl = this.el.querySelector('[data-pzd-color-label]');
        if (lbl) {
          var badge = lbl.querySelector('[data-pzd-color-name]');
          lbl.textContent = this.config.colorLabel + ' ';
          if (badge) lbl.appendChild(badge);
        }
      }

      // Design label (preserve badge)
      if (this.config.designLabel !== undefined) {
        var designLabels = this.el.querySelectorAll('[data-pzd-design-label]');
        designLabels.forEach(function(lbl) {
          var designBadge = lbl.querySelector('[data-pzd-design-name]');
          lbl.textContent = (self.config.designLabel || 'Diseño') + ' ';
          if (designBadge) lbl.appendChild(designBadge);
        });
      }

      // Tabs section label (hidden by default, only show if non-empty)
      var tabsLabel = this.el.querySelector('[data-pzd-tabs-label]');
      if (tabsLabel) {
        var tabsText = this.config.tabsLabel || '';
        tabsLabel.textContent = tabsText;
        tabsLabel.style.display = tabsText ? '' : 'none';
      }

      // Font label
      if (this.config.fontLabel !== undefined) {
        var fontLabels = this.el.querySelectorAll('[data-pzd-font-label]');
        fontLabels.forEach(function(lbl) {
          lbl.textContent = self.config.fontLabel || 'Tipografía';
        });
      }

      // Tab labels
      var sideKeys = ['front', 'back'];
      for (var s = 0; s < sideKeys.length; s++) {
        var key = sideKeys[s];
        var sideCfg = sidesConfig[key] || {};
        if (sideCfg.label) {
          var tabLabel = this.el.querySelector('[data-pzd-tab-label="' + key + '"]');
          if (tabLabel) tabLabel.textContent = sideCfg.label;
        }
      }

      // Preview label
      if (this.config.previewLabel) {
        var lbl = this.el.querySelector('[data-pzd-preview-label]');
        if (lbl) lbl.textContent = this.config.previewLabel;
      }

      // Preview hint
      if (this.config.previewHint !== undefined) {
        var hint = this.el.querySelector('[data-pzd-preview-hint]');
        if (hint) hint.textContent = this.config.previewHint ? '(' + this.config.previewHint + ')' : '';
      }

      // Preview button label
      if (this.config.previewButtonLabel !== undefined) {
        var btnLabels = this.el.querySelectorAll('[data-pzd-preview-btn-label]');
        btnLabels.forEach(function(lbl) {
          lbl.textContent = self.config.previewButtonLabel || 'Ver vista previa';
        });
      }

      // Preview button font size
      if (this.config.previewButtonFontSize) {
        var btns = this.el.querySelectorAll('[data-pzd-open-preview]');
        btns.forEach(function(btn) {
          btn.style.setProperty('--pzd-preview-btn-fs', self.config.previewButtonFontSize + 'px');
        });
      }
    }

    // ============================================================
    // PER-PRODUCT STYLES
    // ============================================================
    _applyStyles() {
      var s = this.config.styles || {};

      var map = {
        'accent':        s.accentColor,
        'accent-hover':  s.accentHover,
        'card-bg':       s.cardBg,
        'border':        s.borderColor,
        'input-bg':      s.inputBg,
        'text':          s.textColor,
        'text-muted':    s.mutedColor,
        'label-weight':  s.labelBold === false ? '500' : '700',
        'label-size':    s.labelSize,
        'tab-size':      s.tabSize,
        'input-size':    s.inputSize,
        'radius':        s.radius,
        'btn-bg':        s.btnBg,
        'btn-text':      s.btnText,
        'btn-radius':    s.btnRadius
      };

      for (var key in map) {
        if (!map.hasOwnProperty(key)) continue;
        if (map[key] != null && map[key] !== '') {
          this.el.style.setProperty('--pzd-' + key, map[key]);
        }
      }
    }

    // ============================================================
    // RENDER: SHAPE SELECTOR
    // ============================================================
    _renderShapeSelector() {
      var container = this.el.querySelector('[data-pzd-shape-selector]');
      if (!container) return;
      var self = this;

      container.innerHTML = '';
      // Disponibles primero, agotadas al final (sort estable: respeta el orden manual dentro de cada grupo)
      var shapes = (this.config.shapes || []).slice().sort(function(a, b) {
        return (self._shapeHasAnyAvailableColor(a) ? 0 : 1) - (self._shapeHasAnyAvailableColor(b) ? 0 : 1);
      });
      shapes.forEach(function(shape) {
        if (shape.available === false) return;
        var soldOut = !self._shapeHasAnyAvailableColor(shape);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pzd__shape-btn';
        if (self.config.shapeSize) {
          btn.style.setProperty('--pzd-shape-size', self.config.shapeSize + 'px');
        }
        if (self.state.shape === shape.id) btn.classList.add('is-active');
        if (soldOut) btn.classList.add('is-unavailable');
        btn.setAttribute('data-pzd-shape-id', shape.id);
        btn.setAttribute('aria-label', shape.name + (soldOut ? ' (Agotado)' : ''));

        var innerHtml;
        if (shape.thumbnail) {
          innerHtml = '<img class="pzd__shape-img" src="' + shape.thumbnail + '" alt="' + shape.name + '">';
        } else {
          innerHtml = '<div class="pzd__shape-inner" style="background:' + (shape.hex || '#ccc') + '"></div>';
        }

        btn.innerHTML = innerHtml;
        container.appendChild(btn);
      });

      this._updateShapeBadge();
    }

    _updateShapeBadge() {
      var badge = this.el.querySelector('[data-pzd-shape-name]');
      if (!badge) return;
      var shape = this._getActiveShape();
      badge.textContent = shape ? shape.name : '';
    }

    // ============================================================
    // SELECT SHAPE
    // ============================================================
    _selectShape(shapeId) {
      // No permitir elegir una forma sin ningún color con stock
      var target = (this.config.shapes || []).find(function(s) { return s.id === shapeId; });
      if (target && !this._shapeHasAnyAvailableColor(target)) return;

      this.state.shape = shapeId;

      // Update active class on shape buttons
      this.el.querySelectorAll('[data-pzd-shape-id]').forEach(function(btn) {
        btn.classList.toggle('is-active', btn.getAttribute('data-pzd-shape-id') === shapeId);
      });

      // Get new shape and select first available color (preferir uno con stock real)
      var self = this;
      var shape = this._getActiveShape();
      if (shape) {
        var shapeColors = shape.colors || [];
        var firstColor = shapeColors.find(function(c) {
          return c.available !== false && self._isVariantAvailable(shape.name, c.name);
        });
        if (!firstColor) firstColor = shapeColors.find(function(c) { return c.available !== false; });
        this.state.color = firstColor ? firstColor.id : null;
      } else {
        this.state.color = null;
      }

      // Re-render colors for the new shape
      this._renderColors();
      this._updateBadge();
      this._updateShapeBadge();

      // Show/hide colors section
      var colorSection = this.el.querySelector('[data-pzd-section="colors"]');
      if (colorSection) {
        var activeShape = this._getActiveShape();
        var shapeColors = activeShape ? (activeShape.colors || []) : [];
        colorSection.style.display = shapeColors.length > 0 ? '' : 'none';
      }

      // Reload product image and redraw
      this._loadProductImage();

      // Reload design images for new color
      this._reloadDesignImages();

      // Update hidden inputs
      this._updateVariantId();
      this._injectHiddenInputs();
      this._updateStatus();
    }

    // ============================================================
    // RENDER: COLORS (from active shape)
    // ============================================================
    _renderColors() {
      var container = this.el.querySelector('[data-pzd-colors]');
      if (!container) return;
      var self = this;

      container.innerHTML = '';
      var shape = this._getActiveShape();
      if (!shape) return;

      // "Disponible" = habilitado en el admin Y con stock real en Shopify (si el sync está activo)
      var isAvail = function(c) {
        return c.available !== false && self._isVariantAvailable(shape.name, c.name);
      };

      // Disponibles primero, agotados al final (sort estable: respeta el orden manual dentro de cada grupo)
      var colors = (shape.colors || []).slice().sort(function(a, b) {
        return (isAvail(a) ? 0 : 1) - (isAvail(b) ? 0 : 1);
      });
      colors.forEach(function(c) {
        var soldOut = !isAvail(c);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pzd__color-btn';
        if (self.config.colorSize) {
          btn.style.setProperty('--pzd-color-size', self.config.colorSize + 'px');
        }
        if (self.state.color === c.id) btn.classList.add('is-active');
        if (soldOut) btn.classList.add('is-unavailable');
        btn.dataset.colorId = c.id;
        btn.setAttribute('aria-label', c.name + (soldOut ? ' (Agotado)' : ''));

        var innerHtml;
        if (c.thumbnail) {
          innerHtml = '<img class="pzd__color-img" src="' + c.thumbnail + '" alt="' + c.name + '">';
        } else {
          innerHtml = '<div class="pzd__color-inner" style="background:' + c.hex + '"></div>';
        }

        btn.innerHTML =
          innerHtml +
          '<span class="pzd__color-name">' + c.name + (soldOut ? ' (Agotado)' : '') + '</span>';
        container.appendChild(btn);
      });
    }

    // ============================================================
    // SELECT COLOR
    // ============================================================
    _selectColor(id) {
      // No permitir elegir un color agotado
      var shape = this._getActiveShape();
      var target = shape ? (shape.colors || []).find(function(c) { return c.id === id; }) : null;
      if (target && (target.available === false || !this._isVariantAvailable(shape.name, target.name))) return;

      this.state.color = id;
      this.el.querySelectorAll('[data-color-id]').forEach(function(b) {
        b.classList.toggle('is-active', b.dataset.colorId === id);
      });
      this._updateBadge();
      this._loadProductImage();

      // Reload design images for the new color
      this._reloadDesignImages();

      this._updateVariantId();
      this._injectHiddenInputs();
    }

    _updateBadge() {
      var badge = this.el.querySelector('[data-pzd-color-name]');
      if (!badge) return;
      var color = this._getActiveColor();
      badge.textContent = color ? color.name : '';
    }

    _updateDesignBadge(sideKey) {
      var badge = this.el.querySelector('[data-pzd-design-name="' + sideKey + '"]');
      if (!badge) return;
      var ss = this.sideState[sideKey];
      var designName = '';
      if (ss.design) {
        var designs = this.config.designs || [];
        var d = designs.find(function(des) { return des.id === ss.design; });
        if (d) designName = d.name || '';
      }
      badge.textContent = designName;
    }

    // ============================================================
    // RENDER: DESIGN GALLERY (per side)
    // ============================================================
    _renderDesignGallery(sideKey) {
      var container = this.el.querySelector('[data-pzd-design-gallery="' + sideKey + '"]');
      if (!container) return;
      var self = this;

      container.innerHTML = '';
      var designs = this.config.designs || [];
      var ss = this.sideState[sideKey];

      designs.forEach(function(design) {
        if (design.available === false) return;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pzd__design-btn';
        if (self.config.designSize) {
          btn.style.setProperty('--pzd-design-size', self.config.designSize + 'px');
        }
        if (ss.design === design.id) btn.classList.add('is-active');
        btn.setAttribute('data-pzd-design-id', design.id);
        btn.setAttribute('data-pzd-design-side', sideKey);
        btn.setAttribute('aria-label', design.name);

        var thumb = design.thumbnail || '';
        btn.innerHTML = '<img class="pzd__design-img" src="' + thumb + '" alt="' + (design.name || '') + '">';
        container.appendChild(btn);
      });

      self._updateDesignBadge(sideKey);
    }

    // ============================================================
    // SELECT DESIGN
    // ============================================================
    _selectDesign(sideKey, designId) {
      this.sideState[sideKey].design = designId;

      // Update active class on design buttons for this side
      var container = this.el.querySelector('[data-pzd-design-gallery="' + sideKey + '"]');
      if (container) {
        container.querySelectorAll('[data-pzd-design-id]').forEach(function(btn) {
          btn.classList.toggle('is-active', btn.getAttribute('data-pzd-design-id') === designId);
        });
      }

      // Load design image for current color, then redraw
      var self = this;
      var colorId = this.state.color;
      if (designId && colorId) {
        this._loadDesignImage(designId, colorId, function() {
          self._drawCanvas();
        });
      } else {
        this._drawCanvas();
      }

      this._updateDesignBadge(sideKey);
      this._injectHiddenInputs();
      this._updateStatus();
    }

    // ============================================================
    // RELOAD DESIGN IMAGES — when color changes
    // ============================================================
    _reloadDesignImages() {
      var self = this;
      var colorId = this.state.color;
      if (!colorId) return;

      var sideKeys = ['front', 'back'];
      var pending = 0;

      for (var s = 0; s < sideKeys.length; s++) {
        var key = sideKeys[s];
        var ss = this.sideState[key];
        if (ss.mode === 'design' && ss.design) {
          pending++;
          this._loadDesignImage(ss.design, colorId, function() {
            pending--;
            if (pending <= 0) {
              self._drawCanvas();
            }
          });
        }
      }

      // If no designs to reload, just redraw
      if (pending === 0) {
        this._drawCanvas();
      }
    }

    // ============================================================
    // RENDER: FONT PICKER (custom dropdown with font preview)
    // ============================================================
    _renderFontSelector() {
      var fonts = this.config.fonts || [];
      var self = this;

      var pickers = this.el.querySelectorAll('[data-pzd-font-picker]');
      pickers.forEach(function(picker) {
        var dropdown = picker.querySelector('[data-pzd-font-dropdown]');
        var preview = picker.querySelector('[data-pzd-font-picker-preview]');
        if (!dropdown) return;

        dropdown.innerHTML = '';
        fonts.forEach(function(f) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'pzd__font-picker-option';
          if (f.family === self.state.font) btn.classList.add('is-active');
          btn.dataset.fontFamily = f.family;
          btn.textContent = f.label || f.family;
          btn.style.fontFamily = '"' + f.family + '", monospace';
          btn.style.textTransform = f.uppercase !== false ? 'uppercase' : 'none';
          btn.style.letterSpacing = (f.letterSpacing || 0) + 'em';
          btn.style.wordSpacing = (f.wordSpacing || 0) + 'em';
          dropdown.appendChild(btn);
        });

        // Set initial preview
        var current = fonts.find(function(f) { return f.family === self.state.font; });
        if (preview && current) {
          preview.textContent = current.label || current.family;
          preview.style.fontFamily = '"' + current.family + '", monospace';
          preview.style.textTransform = current.uppercase !== false ? 'uppercase' : 'none';
          preview.style.letterSpacing = (current.letterSpacing || 0) + 'em';
          preview.style.wordSpacing = (current.wordSpacing || 0) + 'em';
        }
      });
    }

    _updateFontPickerState() {
      var fonts = this.config.fonts || [];
      var self = this;
      var current = fonts.find(function(f) { return f.family === self.state.font; });

      var pickers = this.el.querySelectorAll('[data-pzd-font-picker]');
      pickers.forEach(function(picker) {
        var preview = picker.querySelector('[data-pzd-font-picker-preview]');
        if (preview && current) {
          preview.textContent = current.label || current.family;
          preview.style.fontFamily = '"' + current.family + '", monospace';
          preview.style.textTransform = current.uppercase !== false ? 'uppercase' : 'none';
          preview.style.letterSpacing = (current.letterSpacing || 0) + 'em';
          preview.style.wordSpacing = (current.wordSpacing || 0) + 'em';
        }

        var options = picker.querySelectorAll('.pzd__font-picker-option');
        options.forEach(function(opt) {
          opt.classList.toggle('is-active', opt.dataset.fontFamily === self.state.font);
        });

        picker.classList.remove('is-open');
      });
    }

    // ============================================================
    // TEXTAREA FONT — apply selected font to textareas
    // ============================================================
    _updateTextareaFonts() {
      this._updateTextareaFont('front');
      this._updateTextareaFont('back');
    }

    _updateTextareaFont(sideKey) {
      var textarea = this.el.querySelector('[data-pzd-textarea="' + sideKey + '"]');
      if (!textarea) return;
      var fontCfg = this._getFontConfig();
      var family = this.state.font || 'monospace';
      textarea.style.fontFamily = '"' + family + '", monospace';
      textarea.style.textTransform = fontCfg.uppercase !== false ? 'uppercase' : 'none';
      textarea.style.letterSpacing = (fontCfg.letterSpacing || 0) + 'em';
      textarea.style.wordSpacing = (fontCfg.wordSpacing || 0) + 'em';
      textarea.style.lineHeight = String(fontCfg.lineHeight || 1.5);
    }

    // ============================================================
    // GET CURRENT FONT CONFIG — returns the full font object
    // ============================================================
    _getFontConfig() {
      var fonts = this.config.fonts || [];
      var self = this;
      return fonts.find(function(f) { return f.family === self.state.font; }) || {};
    }

    // Remueve emojis (emoticons, símbolos, banderas, variation selectors, ZWJ).
    // Preserva ♥ (U+2665) porque es un carácter grabable, no un emoji a color.
    _stripEmojis(str) {
      if (!str) return str;
      return str.replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{2664}\u{2666}-\u{27BF}\u{2300}-\u{23FF}\u{FE00}-\u{FE0F}\u{1F1E6}-\u{1F1FF}\u{200D}\u{20E3}\u{2B00}-\u{2BFF}]/gu, '');
    }

    // ============================================================
    // CHAR COUNT — based on selected font's maxChars
    // ============================================================
    _getMaxChars() {
      var f = this._getFontConfig();
      return f.maxChars || 120;
    }

    _updateCharCount(sideKey) {
      var counter = this.el.querySelector('[data-pzd-char-count="' + sideKey + '"]');
      if (!counter) return;

      var maxChars = this._getMaxChars();
      var ss = this.sideState[sideKey];
      var remaining = maxChars - ss.text.length;

      counter.textContent = remaining + ' / ' + maxChars;
      counter.classList.remove('is-warning', 'is-full');
      if (remaining <= 0) counter.classList.add('is-full');
      else if (remaining <= 10) counter.classList.add('is-warning');
    }

    // ============================================================
    // DRAWING — single canvas with both sides
    // ============================================================
    _drawCanvas() {
      if (!this.canvas || !this.ctx) return;

      if (!this._bgImg) {
        // Placeholder
        this.canvas.width = 800;
        this.canvas.height = 400;
        this.ctx.fillStyle = '#e8e5e0';
        this.ctx.fillRect(0, 0, 800, 400);
        this.ctx.fillStyle = '#aaa';
        this.ctx.font = '14px sans-serif';
        this.ctx.textAlign = 'center';
        this.ctx.fillText('Sin imagen de producto', 400, 200);
        return;
      }

      var w = this._bgImg.naturalWidth;
      var h = this._bgImg.naturalHeight;
      this.canvas.width = w;
      this.canvas.height = h;

      this.ctx.clearRect(0, 0, w, h);

      var sideKeys = ['front', 'back'];
      var s, key, ss;

      // Phase 1: Product image at FULL SIZE
      this.ctx.drawImage(this._bgImg, 0, 0, w, h);

      // Phase 2: Design PNGs ON TOP of product (for sides in design mode)
      for (s = 0; s < sideKeys.length; s++) {
        key = sideKeys[s];
        ss = this.sideState[key];
        if (ss.mode === 'design' && ss.design) {
          this._drawDesignOnCanvas(key, w, h);
        }
      }

      // Phase 3: Text ON TOP of everything (for sides in text mode)
      for (s = 0; s < sideKeys.length; s++) {
        key = sideKeys[s];
        ss = this.sideState[key];
        if (ss.mode === 'text' && ss.text.trim()) {
          this._drawTextOnCanvas(key, w, h);
        }
      }

      // Schedule preview upload
      this._schedulePreviewUpload();
    }

    // ============================================================
    // DRAW DESIGN — design PNG clipped to design area
    // ============================================================
    _drawDesignOnCanvas(sideKey, w, h) {
      var ss = this.sideState[sideKey];
      if (!ss.design) return;

      var colorId = this.state.color;
      var cacheKey = ss.design + '_' + colorId;
      var designImg = this._designImages[cacheKey];

      if (!designImg || !designImg.complete || designImg.naturalWidth === 0) return;

      var area = this._getDesignArea(sideKey);
      var areaX = w * area.x;
      var areaY = h * area.y;
      var areaW = w * area.width;
      var areaH = h * area.height;

      // Contain fit — entire design visible, adapts to area proportions
      var imgW = designImg.naturalWidth;
      var imgH = designImg.naturalHeight;
      // Escala propia del diseño (1 = tamaño normal dentro del área).
      var designObj = (this.config.designs || []).find(function(d) { return d.id === ss.design; });
      var userScale = (designObj && typeof designObj.scale === 'number') ? designObj.scale : 1;
      var containScale = Math.min(areaW / imgW, areaH / imgH) * userScale;
      var drawW = imgW * containScale;
      var drawH = imgH * containScale;

      // Center in area
      var cx = areaX + (areaW - drawW) / 2;
      var cy = areaY + (areaH - drawH) / 2;

      this.ctx.save();

      // Draw centered (no clip needed with contain fit)
      this.ctx.drawImage(designImg, cx, cy, drawW, drawH);

      this.ctx.restore();
    }

    // ============================================================
    // DRAW TEXT — auto-shrink free text with word-wrap
    // ============================================================
    _drawTextOnCanvas(sideKey, w, h) {
      var sidesConfig = this.config.sides || {};
      var sideCfg = sidesConfig[sideKey] || {};
      var textCfg = sideCfg.text || {};
      var area = this._getTextArea(sideKey);
      var ss = this.sideState[sideKey];
      var fontCfg = this._getFontConfig();

      var family = this.state.font || 'monospace';
      var align = 'center';

      // Text color from active color config (admin-controlled per product color)
      var color = this._getActiveColor();
      var textColor = (color && color.textColor) ? color.textColor : (textCfg.color || '#000000');

      var shadowColor = textCfg.shadowColor || 'rgba(255, 255, 255, 0.3)';
      var sx = textCfg.shadowOffsetX != null ? textCfg.shadowOffsetX : 1;
      var sy = textCfg.shadowOffsetY != null ? textCfg.shadowOffsetY : 1;
      var lineHeightRatio = fontCfg.lineHeight || 1.4;

      var areaX = w * area.x;
      var areaY = h * area.y;
      var areaW = w * area.width;
      var areaH = h * area.height;

      var maxPx = Math.round(w * (textCfg.maxFontSize || 0.07));
      var minPx = Math.round(w * (textCfg.minFontSize || 0.025));

      // Split text by newlines — uppercase depends on font config
      var textStr = fontCfg.uppercase !== false ? ss.text.toUpperCase() : ss.text;
      var rawLines = textStr.split('\n');

      // Apply per-font spacing BEFORE measuring
      this.ctx.save();
      if (fontCfg.letterSpacing) {
        this.ctx.letterSpacing = fontCfg.letterSpacing + 'em';
      }
      if (fontCfg.wordSpacing) {
        this.ctx.wordSpacing = fontCfg.wordSpacing + 'em';
      }

      // Find fitting font size with word-wrap
      var fontSize = this._findFittingFontSize(rawLines, areaW, areaH, minPx, maxPx, family, lineHeightRatio);

      this.ctx.textAlign = align;
      this.ctx.textBaseline = 'middle';
      this.ctx.font = 'bold ' + fontSize + 'px "' + family + '", monospace';

      // Word-wrap all lines at the found font size
      var wrappedLines = [];
      for (var i = 0; i < rawLines.length; i++) {
        var line = rawLines[i];
        if (!line.trim()) { wrappedLines.push(''); continue; }
        var wrapped = this._wrapText(line, areaW);
        for (var j = 0; j < wrapped.length; j++) {
          wrappedLines.push(wrapped[j]);
        }
      }

      // X anchor
      var x;
      if (align === 'left') x = areaX;
      else if (align === 'center') x = areaX + areaW / 2;
      else x = areaX + areaW;

      // Vertical centering
      var totalH = wrappedLines.length * fontSize * lineHeightRatio;
      var startY = areaY + (areaH - totalH) / 2 + fontSize * lineHeightRatio / 2;
      // Offset vertical por fuente (en em, multiplicado por fontSize para convertir a px)
      startY += (fontCfg.yOffset || 0) * fontSize;

      for (var i = 0; i < wrappedLines.length; i++) {
        var text = wrappedLines[i];
        if (!text) continue;
        var y = startY + i * fontSize * lineHeightRatio;

        // Shadow
        this.ctx.fillStyle = shadowColor;
        this.ctx.fillText(text, x + sx, y + sy);
        // Main text
        this.ctx.fillStyle = textColor;
        this.ctx.fillText(text, x, y);
      }

      this.ctx.restore();
    }

    _wrapText(text, maxWidth) {
      var words = text.split(' ');
      var lines = [];
      var current = '';

      for (var i = 0; i < words.length; i++) {
        var word = words[i];

        // If a single word is wider than maxWidth, break it character by character
        if (this.ctx.measureText(word).width > maxWidth) {
          if (current) { lines.push(current); current = ''; }
          var chunk = '';
          for (var c = 0; c < word.length; c++) {
            var testChunk = chunk + word[c];
            if (this.ctx.measureText(testChunk).width > maxWidth && chunk) {
              lines.push(chunk);
              chunk = word[c];
            } else {
              chunk = testChunk;
            }
          }
          current = chunk;
          continue;
        }

        var test = current ? current + ' ' + word : word;
        if (this.ctx.measureText(test).width > maxWidth && current) {
          lines.push(current);
          current = word;
        } else {
          current = test;
        }
      }
      if (current) lines.push(current);
      return lines;
    }

    _findFittingFontSize(rawLines, areaW, areaH, minPx, maxPx, family, lineHeightRatio) {
      if (rawLines.length === 0 || rawLines.every(function(l) { return !l.trim(); })) return maxPx;

      var lo = minPx, hi = maxPx, best = minPx;

      for (var iter = 0; iter < 20; iter++) {
        var mid = Math.round((lo + hi) / 2);
        if (mid === lo) break;

        this.ctx.font = 'bold ' + mid + 'px "' + family + '", monospace';

        var totalWrapped = 0;
        var fits = true;
        for (var i = 0; i < rawLines.length; i++) {
          var line = rawLines[i];
          if (!line.trim()) { totalWrapped++; continue; }
          var wrapped = this._wrapText(line, areaW);
          totalWrapped += wrapped.length;
          // Check that each wrapped line actually fits the width
          for (var j = 0; j < wrapped.length; j++) {
            if (this.ctx.measureText(wrapped[j]).width > areaW) { fits = false; }
          }
        }

        var totalH = totalWrapped * mid * lineHeightRatio;
        if (totalH <= areaH && fits) { best = mid; lo = mid; }
        else { hi = mid; }
      }

      return best;
    }

    // ============================================================
    // PREVIEW MODAL
    // ============================================================
    _openPreviewModal() {
      // Primera vez: mover el modal al body para escapar de ancestros con
      // transform/filter/perspective que rompen position:fixed (común en temas Shopify).
      if (!this.previewModal) {
        this.previewModal = this.el.querySelector('[data-pzd-preview-modal]');
        if (!this.previewModal) return;
        // Copiar las variables CSS (--pzd-*) del root al modal ANTES de moverlo.
        // Sin esto el modal pierde las variables heredadas (accent color, etc.).
        var rootStyle = this.el.style;
        for (var i = 0; i < rootStyle.length; i++) {
          var prop = rootStyle[i];
          if (prop && prop.indexOf('--') === 0) {
            this.previewModal.style.setProperty(prop, rootStyle.getPropertyValue(prop));
          }
        }
        document.body.appendChild(this.previewModal);
      }
      var modal = this.previewModal;

      var srcCanvas = this.el.querySelector('[data-pzd-canvas]');
      var modalCanvas = modal.querySelector('[data-pzd-preview-modal-canvas]');
      if (srcCanvas && modalCanvas) {
        modalCanvas.width = srcCanvas.width;
        modalCanvas.height = srcCanvas.height;
        var ctx = modalCanvas.getContext('2d');
        ctx.drawImage(srcCanvas, 0, 0);
      }

      modal.style.display = '';
      document.body.style.overflow = 'hidden';
    }

    _closePreviewModal() {
      var modal = this.previewModal || this.el.querySelector('[data-pzd-preview-modal]');
      if (modal) modal.style.display = 'none';
      document.body.style.overflow = '';
    }

    // ============================================================
    // EVENTS
    // ============================================================
    _bindEvents() {
      var self = this;

      // Shape click
      this.el.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-pzd-shape-id]');
        if (btn) {
          self._selectShape(btn.getAttribute('data-pzd-shape-id'));
        }
      });

      // Color click
      this.el.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-color-id]');
        if (btn && !btn.classList.contains('is-unavailable')) {
          self._selectColor(btn.dataset.colorId);
        }
      });

      // Mode toggle click (design / text)
      this.el.addEventListener('click', function(e) {
        var modeBtn = e.target.closest('[data-pzd-mode]');
        if (!modeBtn) return;
        var toggle = modeBtn.closest('[data-pzd-mode-toggle]');
        if (!toggle) return;
        var sideKey = toggle.dataset.pzdModeToggle;
        var mode = modeBtn.dataset.pzdMode;
        self._applyMode(sideKey, mode);
        self._injectHiddenInputs();
        self._updateStatus();
      });

      // Design gallery click
      this.el.addEventListener('click', function(e) {
        var designBtn = e.target.closest('[data-pzd-design-id]');
        if (designBtn) {
          var sideKey = designBtn.getAttribute('data-pzd-design-side');
          var designId = designBtn.getAttribute('data-pzd-design-id');
          if (sideKey && designId) {
            self._selectDesign(sideKey, designId);
          }
        }
      });

      // Textarea input
      this.el.addEventListener('input', function(e) {
        if (e.target.hasAttribute('data-pzd-textarea')) {
          var sideKey = e.target.dataset.pzdTextarea;
          // Bloquear emojis si el admin los prohibió
          if (self.config.allowEmojis === false) {
            var cleaned = self._stripEmojis(e.target.value);
            if (cleaned !== e.target.value) e.target.value = cleaned;
          }
          var maxChars = self._getMaxChars();
          var val = e.target.value;

          // Enforce max chars
          if (val.length > maxChars) {
            e.target.value = val.substring(0, maxChars);
          }

          self.sideState[sideKey].text = e.target.value;
          e.target.classList.remove('is-error');
          self._updateCharCount(sideKey);
          self._drawCanvas();
          self._injectHiddenInputs();
          self._updateStatus();
        }
      });

      // Font picker — toggle dropdown
      this.el.addEventListener('click', function(e) {
        var pickerBtn = e.target.closest('[data-pzd-font-picker-btn]');
        if (pickerBtn) {
          var picker = pickerBtn.closest('[data-pzd-font-picker]');
          if (picker) {
            // Close all other pickers first
            self.el.querySelectorAll('[data-pzd-font-picker]').forEach(function(p) {
              if (p !== picker) p.classList.remove('is-open');
            });
            picker.classList.toggle('is-open');
          }
          return;
        }

        // Font picker — select option
        var option = e.target.closest('.pzd__font-picker-option');
        if (option) {
          var family = option.dataset.fontFamily;
          if (family && family !== self.state.font) {
            self.state.font = family;
            self._updateFontPickerState();
            self._updateTextareaFonts();
            self._updateCharCount('front');
            self._updateCharCount('back');

            // Enforce max chars on both textareas
            var maxChars = self._getMaxChars();
            ['front', 'back'].forEach(function(sk) {
              var ta = self.el.querySelector('[data-pzd-textarea="' + sk + '"]');
              if (ta && ta.value.length > maxChars) {
                ta.value = ta.value.substring(0, maxChars);
                self.sideState[sk].text = ta.value;
              }
            });

            self._drawCanvas();
            self._injectHiddenInputs();
          } else {
            // Same font — just close
            var picker = option.closest('[data-pzd-font-picker]');
            if (picker) picker.classList.remove('is-open');
          }
          return;
        }

        // Close picker if clicking outside
        if (!e.target.closest('[data-pzd-font-picker]')) {
          self.el.querySelectorAll('[data-pzd-font-picker]').forEach(function(p) {
            p.classList.remove('is-open');
          });
        }
      });

      // Close font picker on outside click (document level)
      document.addEventListener('click', function(e) {
        if (!e.target.closest('[data-pzd-font-picker]')) {
          self.el.querySelectorAll('[data-pzd-font-picker]').forEach(function(p) {
            p.classList.remove('is-open');
          });
        }
      });

      // ---- Preview button -> open modal ----
      this.el.addEventListener('click', function(e) {
        if (e.target.closest('[data-pzd-open-preview]')) {
          self._openPreviewModal();
        }
      });

      // ---- Close preview modal ----
      // Listener en document porque el modal vive en <body> (no en this.el)
      // una vez que se abre por primera vez.
      document.addEventListener('click', function(e) {
        if (e.target.closest('[data-pzd-preview-modal-close]')) {
          var modalClicked = e.target.closest('[data-pzd-preview-modal]');
          if (!modalClicked || modalClicked !== self.previewModal) return;
          self._closePreviewModal();
        }
      });
    }

    // ============================================================
    // PREVIEW UPLOAD — background debounce (single canvas)
    // ============================================================
    _schedulePreviewUpload() {
      var self = this;
      if (this._uploadTimer) clearTimeout(this._uploadTimer);
      this._uploadTimer = setTimeout(function() {
        self._doUploadPreview();
      }, 1500);
    }

    async _doUploadPreview() {
      if (!this.canvas || !this._bgImg) return;

      var uploadUrl = this.el.dataset.pzdUploadUrl;
      var uploadKey = this.el.dataset.pzdUploadKey;
      if (!uploadUrl || !uploadKey) return;

      try {
        var dataUrl = __pzSobreBlanco(this.canvas).toDataURL('image/jpeg', 0.8);

        var controller = new AbortController();
        var timeoutId = setTimeout(function() { controller.abort(); }, 15000);

        var response = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Upload-Key': uploadKey
          },
          body: JSON.stringify({
            image: dataUrl,
            productId: this.el.dataset.pzdVariantId || '',
            variantId: (this.state.variantId || '') + '-engrave-preview'
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          var result = await response.json();
          if (result.url) {
            this._previewUrl = result.url;
            this._injectHiddenInputs();
          }
        }
      } catch (err) {
        console.warn('[PZD-ENG] Preview upload error:', err);
      }
    }

    // ============================================================
    // STATUS INDICATOR
    // ============================================================
    _updateStatus() {
      var statusEl = this.el.querySelector('[data-pzd-status]');
      if (!statusEl) return;

      var sidesConfig = this.config.sides || {};
      var sideKeys = ['front', 'back'];
      var issues = [];
      var allGood = true;

      for (var s = 0; s < sideKeys.length; s++) {
        var key = sideKeys[s];
        var ss = this.sideState[key];
        var sideCfg = sidesConfig[key] || {};
        var sideLabel = sideCfg.label || (key === 'front' ? 'Adelante' : 'Atrás');

        if (ss.mode === 'text' && !ss.text.trim()) {
          issues.push(sideLabel + ': escribe tu texto');
          allGood = false;
        } else if (ss.mode === 'design' && !ss.design) {
          issues.push(sideLabel + ': selecciona un diseño');
          allGood = false;
        }

        // Update tab icon
        var icon = this.el.querySelector('[data-pzd-tab-icon="' + key + '"]');
        if (icon) {
          icon.classList.remove('is-ready', 'is-pending');
          var sideReady = (ss.mode === 'text' && ss.text.trim()) ||
                          (ss.mode === 'design' && ss.design);
          icon.classList.add(sideReady ? 'is-ready' : 'is-pending');
        }
      }

      statusEl.classList.add('is-visible');
      statusEl.classList.remove('is-pending', 'is-ready');

      if (allGood) {
        statusEl.classList.add('is-ready');
        statusEl.textContent = '\u2713\u2002Listo \u2014 puedes hacer tu pedido';
      } else {
        statusEl.classList.add('is-pending');
        statusEl.textContent = 'Pendiente \u2014 ' + issues.join('; ');
      }
    }

    // ============================================================
    // VALIDATION
    // ============================================================
    _validate() {
      this._hideMsg();
      var valid = true;
      var issues = [];
      var firstFailSide = null;
      var sidesConfig = this.config.sides || {};

      // Stock real de Shopify (solo si el sync está activo)
      if (this.config.enableStockSync === true) {
        // Producto simple agotado (stock global) → bloquear todo con un mensaje claro.
        if (this._isSimpleProduct() && !this.productAvailable) {
          this._showMsg('error', 'Este producto está agotado.');
          return false;
        }
        var stShape = this._getActiveShape();
        var stColor = this._getActiveColor();
        if (stColor && !this._isVariantAvailable(stShape ? stShape.name : '', stColor.name)) {
          this._showMsg('error', 'Esta combinación está agotada. Elegí otra.');
          return false;
        }
      }

      var sideKeys = ['front', 'back'];
      for (var s = 0; s < sideKeys.length; s++) {
        var key = sideKeys[s];
        var ss = this.sideState[key];
        var sideCfg = sidesConfig[key] || {};
        var sideLabel = sideCfg.label || (key === 'front' ? 'Adelante' : 'Atrás');

        if (ss.mode === 'text') {
          if (!ss.text.trim()) {
            valid = false;
            issues.push(sideLabel + ': escribe tu texto');
            var textarea = this.el.querySelector('[data-pzd-textarea="' + key + '"]');
            if (textarea) textarea.classList.add('is-error');
            if (!firstFailSide) firstFailSide = key;
          }
        } else if (ss.mode === 'design') {
          if (!ss.design) {
            valid = false;
            issues.push(sideLabel + ': selecciona un diseño');
            if (!firstFailSide) firstFailSide = key;
          }
        }
      }

      if (!valid) {
        if (firstFailSide) this._switchSide(firstFailSide);
        this._showMsg('error', 'Completa: ' + issues.join('; '));
      }

      return valid;
    }

    // ============================================================
    // UI HELPERS
    // ============================================================
    _showMsg(type, text) {
      this.el.querySelectorAll('[data-pzd-msg]').forEach(function(m) { m.classList.remove('is-visible'); });
      var el = this.el.querySelector('[data-pzd-msg="' + type + '"]');
      if (el) { el.textContent = text; el.classList.add('is-visible'); }
    }

    _hideMsg() {
      this.el.querySelectorAll('[data-pzd-msg]').forEach(function(m) { m.classList.remove('is-visible'); });
    }
  }

  // ============================================================
  // AUTO-INIT
  // ============================================================
  function initAll() {
    document.querySelectorAll('[data-product-personalizer-dual-engrave]').forEach(function(el) {
      if (!el._pzdeng) el._pzdeng = new ProductPersonalizerDualEngrave(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', function(e) {
    var el = e.target.querySelector('[data-product-personalizer-dual-engrave]');
    if (el) el._pzdeng = new ProductPersonalizerDualEngrave(el);
  });

})();
